<template>
  <modal
    size="lg"
    :submit-button-text="submitBtnText"
    title="Custom fields Library"
    ref="modal"
    :show="show"
    @hidden="$emit('input', false)"
    @submit="onSubmit"
    :is-submit-disabled="selectedCustomFieldIds.length === 0"
    :is-submitting="isSubmitting"
    :hide-footer="showCustomFieldEditor || orgCustomFields.length === 0"
    :hide-header="showCustomFieldEditor"
  >
    <custom-field-editor
      :show-back-button="true"
      :org-id="orgId"
      @back="dismissEditor"
      @saved="dismissEditor"
      @created="onCFCreation"
      @deleted="onCFDeletion"
      :custom-field="currentCustomField"
      v-if="showCustomFieldEditor"
    />
    <div v-else>
      <custom-field-empty-state @new="addNew" v-if="orgCustomFields.length === 0" />
      <div class="table" v-else>
        <div class="table-header d-flex text-prime">
          <div class="table-cell title-cell title-cell-header">Field</div>
          <div class="table-cell type-cell">Type</div>
          <div class="table-cell creator-cell">Created By</div>
          <div class="table-cell action-cell"></div>
        </div>
        <div class="table-body">
          <div
            class="table-row d-flex align-items-center"
            v-for="field in orgCustomFields"
            :key="field.id"
          >
            <div class="table-cell title-cell d-flex align-items-baseline">
              <b-form-checkbox
                :checked="selectedCustomFieldIds.includes(field.id)"
                @change="onFieldSelect(field, $event)"
                name="check-button"
              />
              {{ field.name }}
            </div>
            <div class="table-cell type-cell text-secondary">{{ FIELD_LABEL[field.type] }}</div>
            <div
              class="table-cell creator-cell text-secondary"
              :title="field.creator && field.creator.email"
            >
              {{ getCreatedBy(field) }}
            </div>
            <div class="table-cell d-flex align-items-center action-cell">
              <link-button class="text-medium" @click="triggerEdit(field)">Edit</link-button>
              <link-button class="text-medium" @click="showDeletionConfirmation(field)"
                >Delete</link-button
              >
            </div>
          </div>
        </div>
      </div>
      <max-cfs-paywall-modal
        v-model="isPaywallModalShown"
        :org-id="orgId"
        v-if="isPaywallModalShown"
      />
      <delete-cf-confirm-dialog
        @deleted="setCFAdditionAllowedFlag"
        ref="deleteConfirmModal"
        :custom-field="currentCustomField"
        :org-id="orgId"
      />
    </div>
    <div slot="footer-right" v-if="!showCustomFieldEditor">
      <link-button link-color="brand bold" class="text-medium" @click="addNew"
        >New Custom Field</link-button
      >
    </div>
  </modal>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton.vue'
import CustomFieldEditor from '@/components/CustomFieldEditor.vue'
import CustomFieldEmptyState from '@/components/initiative/CustomFieldEmptyState.vue'
import DeleteCfConfirmDialog from '@/components/initiative/DeleteCfConfirmDialog.vue'
import Modal from '@/components/Modal.vue'
import MaxCfsPaywallModal from '@/components/paywalls/MaxCfsPaywallModal.vue'
import { FIELD_LABEL } from '@/constants/customFields'
import { featureFlags } from '@/constants/featureFlags'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import getTeamSpaceName from '@/utils/getTeamSpaceName'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BFormCheckbox } from 'bootstrap-vue'
import { mapActions } from 'vuex'

/**
 * This shows custom fields library in a Modal. The library shows list of org-level custom fields which can be added to team/project.
 */
export default {
  name: 'CustomFieldsLibraryModal',
  components: {
    CustomFieldEditor,
    MaxCfsPaywallModal,
    CustomFieldEmptyState,
    DeleteCfConfirmDialog,
    LinkButton,
    BFormCheckbox,
    Modal
  },
  mixins: [FeaturesMixin],
  props: ['show', 'orgId', 'projectId', 'teamId', 'addToDisplayFields'],
  model: {
    prop: 'show'
  },
  data() {
    return {
      FIELD_LABEL,
      selectedCustomFieldIds: [],
      isSubmitting: false,
      showCustomFieldEditor: false,
      isPaywallModalShown: false,
      isCFAdditionAllowed: false,
      currentCustomField: null
    }
  },
  computed: {
    orgCustomFields() {
      return this.$store.getters
        .customFieldByOrgId(this.orgId)
        .filter(field => !this.enabledCustomFieldIds?.includes(field.id))
    },
    enabledCustomFieldIds() {
      if (this.projectId) {
        return this.$store.getters
          .initiativeEnabledCustomFieldsByProjectId(this.projectId)
          .map(field => field.id)
      }
      return this.$store.getters
        .initiativeEnabledCustomFieldsByTeamId(this.teamId)
        .map(field => field.id)
    },
    submitBtnText() {
      if (this.projectId) {
        return 'Add to project'
      } else {
        return `Add to ${getTeamSpaceName(this.$store.getters.teamById(this.teamId))}`
      }
    }
  },
  methods: {
    ...mapActions([
      'getEnabledCustomFields',
      'getOrgCustomFields',
      'attachCustomFields',
      'addDisplayInitiativeField'
    ]),
    getCreatedBy(field) {
      if (field.creator) {
        return `${field.creator.firstName} ${field.creator.lastName}`
      }
      return '-'
    },
    hideModal() {
      this.$refs.modal.hide()
    },
    onFieldSelect(field, isSelected) {
      if (isSelected) {
        this.selectedCustomFieldIds.push(field.id)
      } else {
        this.selectedCustomFieldIds = this.selectedCustomFieldIds.filter(id => id !== field.id)
      }
    },
    updateEnabledCFIds() {
      this.enabledCustomFieldIds = this.enabledCustomFields.map(x => x.id)
    },
    async addCustomFieldsToDisplayFields() {
      try {
        await this.addDisplayInitiativeField({
          orgId: this.orgId,
          teamId: this.teamId,
          projectId: this.projectId,
          payload: {
            customFieldDefinitionIds: this.selectedCustomFieldIds
          }
        })
      } catch (e) {
        console.warn('Error while adding to display list', e)
      }
    },
    async onSubmit() {
      try {
        this.isSubmitting = true
        await this.attachCustomFields({
          orgId: this.orgId,
          teamId: this.teamId,
          projectId: this.projectId,
          customFieldDefinitionIds: this.selectedCustomFieldIds
        })
        if (this.addToDisplayFields) {
          await this.addCustomFieldsToDisplayFields()
        }
        showToast('Custom field(s) have been added successfully')
        this.$emit('added', this.selectedCustomFieldIds)
        this.hideModal()
      } catch (e) {
        showAPIError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    showDeletionConfirmation(field) {
      this.currentCustomField = field
      this.$refs.deleteConfirmModal.show()
    },
    triggerEdit(field) {
      this.currentCustomField = field
      this.showCustomFieldEditor = true
    },
    async setCFAdditionAllowedFlag() {
      this.isCFAdditionAllowed = await this.satisfyFeature(
        this.orgId,
        featureFlags.MAX_CUSTOM_FIELD_COUNT
      )
    },
    addNew() {
      this.currentCustomField = null
      if (!this.isCFAdditionAllowed) {
        this.isPaywallModalShown = true
      } else {
        this.showCustomFieldEditor = true
      }
    },
    dismissEditor() {
      this.showCustomFieldEditor = false
    },
    onCFCreation() {
      this.getOrgCustomFields({ orgId: this.orgId })
      this.setCFAdditionAllowedFlag()
      this.dismissEditor()
    },
    onCFDeletion() {
      this.setCFAdditionAllowedFlag()
      this.dismissEditor()
    }
  },
  mounted() {
    this.setCFAdditionAllowedFlag()
    if (!this.orgCustomFields) {
      this.getOrgCustomFields({ orgId: this.orgId })
    }
    if (this.enabledCustomFields) {
      this.updateEnabledCFIds()
    } else {
      this.getEnabledCustomFields({
        orgId: this.orgId,
        teamId: this.teamId,
        projectId: this.projectId
      }).catch(showAPIError)
    }
  }
}
</script>

<style scoped lang="scss">
@import '~@/styles/_styleConfigurations.scss';
.table-row {
  border-bottom: 1px solid $borderPrimary;
}
.title-cell {
  width: 200px;
  flex-grow: 1;
}
.type-cell {
  width: 100px;
}
.creator-cell {
  width: 180px;
}
.action-cell {
  width: 200px;
}
.table-cell {
  padding: 4px;
}
.table-header {
}
.table-header {
  border-bottom: 1px solid $borderPrimary;
}
.title-cell-header {
  padding-left: 28px;
}
</style>
